import React, { useEffect, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { Box } from "@curves/ui-base";
import { MEMBER_TASK_TYPE } from "@icoach/constants/";
import TodoInput from "./TodoInput";
import TodoList from "./TodoList";
import { useMemberProfileApi } from "@apis/useMemberProfileApi";
import { isEmpty, isFunction } from "@util/utils";

//  `todoReducer` 負責管理 `todos` 狀態
const todoReducer = (state, action) => {
	switch (action.type) {
		case "UPDATE_TODO": // 更新整個 todos 清單
			return action.todos;
		case "ADD_TODO": // 新增新的待辦事項
			return [
				{
					taskID: action.payload.taskID,
					taskTitle: action.payload.taskTitle,
					taskType: action.payload.taskType,
					reminderConditionType: action.payload.reminderConditionType,
					isCompleted: false, // 預設新增的待辦事項為未完成
				},
				...state,
			];
		case "TOGGLE_COMPLETED": // 切換待辦事項的完成狀態
			return state.map(todo => (todo.taskID === action.payload ? { ...todo, isCompleted: !todo.isCompleted } : todo));
		case "DELETE_TODO": // 刪除特定待辦事項
			return state.filter(todo => todo.taskID !== action.payload);
		default:
			return state;
	}
};

const TodoManager = React.forwardRef((props, ref) => {
	const { className, memberID, taskType = MEMBER_TASK_TYPE.SERVICETASK, todos: todosProps, options, showInput = false, refresh } = props;

	const { postMemberProfileTasksApi, putMemberProfileTasksApi, deleteMemberProfileTasksApi } = useMemberProfileApi();

	const [todos, dispatch] = React.useReducer(todoReducer, []);

	// 發送請求到後端以新增待辦事項
	const doPostMemberProfileTasksApi = async params => {
		let resp = await postMemberProfileTasksApi(memberID, params);
		if (resp) {
			dispatch({
				type: "ADD_TODO",
				payload: resp,
			});
			isFunction(refresh) && refresh();
		}
	};

	// 發送請求到後端以更新待辦事項的完成狀態
	const doPutMemberProfileTasksApi = async params => {
		let resp = await putMemberProfileTasksApi(memberID, params);

		if (resp) {
			dispatch({ type: "TOGGLE_COMPLETED", payload: params.taskID });
			isFunction(refresh) && refresh();
		}
	};

	// 發送請求到後端以刪除待辦事項
	const doDeleteMemberProfileTasksApi = async taskID => {
		let resp = await deleteMemberProfileTasksApi(memberID, taskID);

		if (resp) {
			dispatch({ type: "DELETE_TODO", payload: taskID });
			isFunction(refresh) && refresh();
		}
	};

	// 處理新增待辦事項
	const handleAddTodo = taskTitle => {
		if (taskTitle) {
			doPostMemberProfileTasksApi({
				taskID: 0,
				taskTitle: taskTitle.toString().trim(),
				taskType,
			});
		} else {
			window.alert("請輸入待辦事項");
		}
	};

	// 處理切換完成狀態
	const handleToggleCompleted = todoData => {
		const { taskID, taskTitle, reminderConditionType } = todoData;
		doPutMemberProfileTasksApi({
			taskID,
			taskTitle,
			taskType,
			reminderConditionType,
			isCompleted: !todoData.isCompleted,
		});
	};

	// 處理刪除待辦事項
	const handleDeleteItem = taskID => {
		doDeleteMemberProfileTasksApi(taskID);
	};

	// 處理更新待辦事項
	const handleUpdateTodo = todos => {
		dispatch({ type: "UPDATE_TODO", todos });
	};

	//  當 `todosProps` 更新時，重新同步 `todos`
	useEffect(
		() => {
			if (!isEmpty(todosProps)) handleUpdateTodo(todosProps);
		},
		// eslint-disable-next-line
		[todosProps]
	);

	useImperativeHandle(
		ref,
		() => ({
			getTodos: () => {
				return todos;
			},
			onUpdatedTodos: todos => {
				handleUpdateTodo(todos);
			},
		}),
		// eslint-disable-next-line
		[todos]
	);

	return (
		<Box className={clsx("todo", className)}>
			{/* 待辦事項輸入框 */}
			{showInput && <TodoInput className="mb-3" onAddTodo={handleAddTodo} options={options} />}
			{/* 待辦事項列表 */}
			<TodoList todos={todos} onToggleCompleted={handleToggleCompleted} onDeleteItem={handleDeleteItem} />
		</Box>
	);
});

TodoManager.propTypes = {
	className: PropTypes.string,
	taskType: PropTypes.number,
	todos: PropTypes.array,
};

export default TodoManager;
